/**
 * Safe storage utilities with namespacing and cleanup
 */

const STORAGE_PREFIX = 'nomera_';
const APP_ENV = process.env.NODE_ENV || 'development';

/**
 * Generate namespaced storage key
 */
function getNamespacedKey(role: string, key: string): string {
  return `${STORAGE_PREFIX}${APP_ENV}:${role}:${key}`;
}

/**
 * Safe localStorage operations with error handling
 */
export const storage = {
  /**
   * Set item with namespacing
   */
  setItem(role: string, key: string, value: string): boolean {
    try {
      const namespacedKey = getNamespacedKey(role, key);
      localStorage.setItem(namespacedKey, value);
      return true;
    } catch (error) {
      console.warn('Failed to set localStorage item:', error);
      return false;
    }
  },

  /**
   * Get item with namespacing
   */
  getItem(role: string, key: string): string | null {
    try {
      const namespacedKey = getNamespacedKey(role, key);
      return localStorage.getItem(namespacedKey);
    } catch (error) {
      console.warn('Failed to get localStorage item:', error);
      return null;
    }
  },

  /**
   * Remove item with namespacing
   */
  removeItem(role: string, key: string): boolean {
    try {
      const namespacedKey = getNamespacedKey(role, key);
      localStorage.removeItem(namespacedKey);
      return true;
    } catch (error) {
      console.warn('Failed to remove localStorage item:', error);
      return false;
    }
  },

  /**
   * Clear all app-specific storage for a role
   */
  clearRole(role: string): boolean {
    try {
      const prefix = `${STORAGE_PREFIX}${APP_ENV}:${role}:`;
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith(prefix)
      );
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.warn('Failed to clear role storage:', error);
      return false;
    }
  },

  /**
   * Clear all app-specific storage
   */
  clearApp(): boolean {
    try {
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith(STORAGE_PREFIX) ||
        key.startsWith('supabase.') ||
        key.includes('onboarding') ||
        key.includes('draft')
      );
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.warn('Failed to clear app storage:', error);
      return false;
    }
  },

  /**
   * Get all keys for a role
   */
  getRoleKeys(role: string): string[] {
    try {
      const prefix = `${STORAGE_PREFIX}${APP_ENV}:${role}:`;
      return Object.keys(localStorage).filter(key => 
        key.startsWith(prefix)
      );
    } catch (error) {
      console.warn('Failed to get role keys:', error);
      return [];
    }
  }
};

/**
 * Safe sessionStorage operations
 */
export const sessionStorage = {
  setItem(key: string, value: string): boolean {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn('Failed to set sessionStorage item:', error);
      return false;
    }
  },

  getItem(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to get sessionStorage item:', error);
      return null;
    }
  },

  removeItem(key: string): boolean {
    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Failed to remove sessionStorage item:', error);
      return false;
    }
  },

  clear(): boolean {
    try {
      window.sessionStorage.clear();
      return true;
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error);
      return false;
    }
  }
};
